import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router';
import { ordersApi } from 'src/api/ordersApi';
import SubHeader from 'src/components/SubHeader';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { CustomButton2 } from 'src/components/widgets/buttons/CustomButton2';
import SearchBar from 'src/components/widgets/searchBar/SearchBar';
import { CLIENT_ORDERS_CREATE } from 'src/constants/constants';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import useHasPermissions from 'src/hooks/useHasPermission';
import useGenericStyles from 'src/styles/useGenericStyles';

const SHOW_BOOK_KEEPING_STORAGE_KEY = 'orders.showBookKeepingRecords';

const readShowBookKeepingPreference = (): boolean => {
    try {
        return localStorage.getItem(SHOW_BOOK_KEEPING_STORAGE_KEY) === 'true';
    } catch {
        return false;
    }
};

function Orders() {
    const classes = useGenericStyles()
    const theme = useTheme()
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(25)
    const [search, setSearch] = useState("")
    const [data, setData] = useState([])
    const [sort, setSort] = useState({});
    const [showBookingKeepingRecords, setShowBookingKeepingRecords] = useState(
        readShowBookKeepingPreference
    );
    const { userData } = useUserProfile();
    const checkPermissions = useHasPermissions();

    const handleShowBookKeepingChange = (checked: boolean) => {
        setShowBookingKeepingRecords(checked);
        setPage(0);
        try {
            localStorage.setItem(SHOW_BOOK_KEEPING_STORAGE_KEY, String(checked));
        } catch {
            // Ignore storage failures (private mode / quota)
        }
    };

    const filteredData = useMemo(
        () =>
            (data || []).filter(
                (row: any) => showBookingKeepingRecords || !row?.isBookkeepingRecord
            ),
        [data, showBookingKeepingRecords]
    );

    const paginatedData = useMemo(
        () => filteredData.slice(page * pageSize, (page + 1) * pageSize),
        [filteredData, page, pageSize]
    );

    const handleSearch = (value: string) => {
        setSearch(value);
        setPage(0); // Reset to first page on search
    };
    const formatDate = (value: string) => {
        const date = new Date(value);

        const month = date.toLocaleString("en-US", { month: "short" }); // Jan, Feb, Mar...
        const day = String(date.getDate()).padStart(2, "0");            // 01–31
        const year = date.getFullYear();

        return `${day} ${month} ${year}`;
    };

    const handleDownloadPDF = async (orderId: string, orderNumber: string) => {
        try {
            toast.loading('Downloading PDF...', { id: 'download-pdf' });
            const pdfBlob = await ordersApi.downloadOrderPDF(orderId);

            // Create a download link and trigger download
            const url = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Order_${orderNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('PDF downloaded successfully', { id: 'download-pdf' });
        } catch (error: any) {
            console.error('Error downloading PDF:', error);
            const errorMessage = error?.message || 'Failed to download PDF';
            toast.error(errorMessage, { id: 'download-pdf' });
        }
    };

    const handleRowClick = (orderData: any) => {
        // Navigate to create-order; detail is loaded via getOrderById
        navigate('/create-order', {
            state: {
                orderId: orderData?._id || orderData?.id,
                orderData,
            }
        });
    };

    const handlePage = (temp: number) => {
        setPage(temp);
    };
    const handlePageSize = (temp: number) => {
        setPageSize(temp);
    };
    const handleSort = (col) => setSort(col);

    const columns = [
        {
            field: 'orderNumber',
            headerName: 'Order #',
            sortable: true,
        },
        {
            field: 'fromOrganization',
            headerName: 'From Organization',
            width: '20%',
            render: (value: any): JSX.Element => <>{value?.name || 'N/A'}</>
        },
        {
            field: 'toOrganization',
            headerName: 'To Organization',
            width: '20%',
            render: (value: any): JSX.Element => <>{value?.name || 'N/A'}</>
        },

        {
            field: 'products',
            headerName: 'Products',
            // sortable: true,
            width: '300px',
            render: (value: any): JSX.Element => <Box sx={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={value.slice(0, 4).map(p => p.name).join(", ")}>
                {value.slice(0, 4).map(p => p.name).join(", ")}
            </Box>

        },
        {
            field: 'productsQuantity',
            headerName: 'Quantity',
            render: (_value: any, row: any): JSX.Element => (
                <>{(row.products || []).reduce((sum, item) => sum + (item.quantity || 0), 0)}</>
            ),
        },
        {
            field: 'total',
            headerName: 'Price',
            sortable: true,
            render: (value: any): JSX.Element => <>{value > 0 ? value + '€' : '-'}</>
            // width: '25%',
        },
        {
            field: 'orderDate',
            headerName: 'Order Date',
            sortable: true,
            // width: '25%',
            render: (value: any): string => value
                ? formatDate(value)
                : 'N/A'

        },
        {
            field: 'status',
            headerName: 'Status',
            sortable: true,
            render: (value: any): JSX.Element => {
                const statusColors: Record<string, { text: string; background: string }> = {
                    pending: {
                        text: "#F59E0B",
                        background: "#FEF3C7",
                    },
                    approved: {
                        text: "#10B981",
                        background: "#D1FAE5",
                    },
                    declined: {
                        text: "#EF4444",
                        background: "#FEE2E2",
                    },
                };
                const colors = statusColors[value?.toLowerCase()] || { text: '#2196f3', background: '#E3F2FD' };
                return (
                    <Box
                        component="span"
                        sx={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: colors.background,
                            color: colors.text,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            textTransform: 'capitalize',
                        }}
                    >
                        {value || 'N/A'}
                    </Box>
                );
            }


        },
        {
            field: 'actions',
            headerName: 'Actions',
            sortable: false,
            render: (value: any, row: any): JSX.Element => (
                <Tooltip title="Download PDF">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadPDF(row._id || row.id, row.orderNumber);
                        }}
                        style={{ color: theme.palette.primary.main }}
                    >
                        <GetAppIcon />
                    </IconButton>
                </Tooltip>
            )
        },

    ]

    const fetchAllOrders = async () => {
        if (!userData?.organization?.id) {
            return;
        }

        setLoading(true);
        try {

            // Fetch all reseller orders so bookkeeping filter can run client-side
            const response = await ordersApi.getResellerOrders(
                userData.organization.id,
                undefined,
                { page: 0, pageSize: 1000 }
            );

            if (response?.data) {
                setData(response.data?.data || response.data || []);
            } else {
                setData([]);
            }

        } catch (e) {
            console.warn(e)
            setData([]);

        } finally {
            setLoading(false);
        }
    }


    useEffect(() => {
        if (userData?.organization?.id) {
            fetchAllOrders()
        }
    }, [userData])


    return (
        <>
            <Helmet>
                <title>
                    Mindtime User Portal | Orders
                </title>
            </Helmet>
            <SubHeader title="Orders" />
            <Box px="30px">
                <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", alignItems: "center", mb: "10px", gap: "16px" }} >
                    <SearchBar
                        value={search}
                        onSearch={handleSearch}
                        placeholderData="Search Order id..."
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showBookingKeepingRecords}
                                onChange={(e) => {
                                    handleShowBookKeepingChange(e.target.checked);
                                }}
                                color="primary"
                                size="small"
                                style={{ padding: '6px' }}
                            />
                        }
                        label={
                            <span style={{ fontSize: '14px', fontWeight: 400, color: '#425466', lineHeight: 1.4 }}>
                                Show Book Keeping Records
                            </span>
                        }
                        style={{
                            marginLeft: 4,
                            marginRight: 8,
                            whiteSpace: 'nowrap',
                        }}
                    />
                    {checkPermissions([CLIENT_ORDERS_CREATE]) &&
                        <CustomButton2
                            backgroundColor={theme.palette.primary.main}
                            className={classes.button}
                            onClick={() => {
                                navigate("/create-order");
                            }}
                        >
                            <AddIcon sx={{
                                marginRight: '5px',
                            }} /> Create Order
                        </CustomButton2>
                    }
                </Box>
                <CustomizedTable
                    heading="All Orders"
                    loading={loading}
                    columns={columns}
                    page={page}
                    handleOnClick={handleRowClick}
                    limit={pageSize}
                    handleLimit={handlePageSize}
                    handlePage={handlePage}
                    data={paginatedData}
                    totalRecords={filteredData.length}
                    handleSort={handleSort}
                    sort={sort}
                    navigateToKey
                    selectRowData
                    customizableColumns
                    getRowStyle={(row) =>
                        row?.isBookkeepingRecord
                            ? { backgroundColor: '#E8E8E8' }
                            : {}
                    }
                />
            </Box>
        </>
    )
}

export default Orders
